import { useMemo, useState } from 'react'
import { Modal } from '@/shared/components/Modal'
import { api } from '@/lib/api'
import {
  BASELINE_ARTEFACTS, buildBaselineRows, defaultSelection, toRequestPolicies, columnCounts,
  applySummary, type BaselineRow,
} from '@/features/policies/lib/baselinePolicies'
import type { PolicyArtefactType, PolicyItem } from '@/lib/apiTypes'
import { useOrgPolicySettings } from '@/lib/hooks'
import s from '@/features/policies/components/BaselinePolicyModal.module.css'

/** Column headings: plural, matching the page's own tab labels. */
const ARTEFACT_LABEL: Record<PolicyArtefactType, string> = {
  module: 'Modules', state: 'State', provider: 'Providers', plan: 'Plans',
}

/** The same artefact used as an adjective in the dialog title - "Recommended provider policies",
 *  not "Recommended providers policies". Matches the template gallery's own titles. */
const ARTEFACT_ADJECTIVE: Record<PolicyArtefactType, string> = {
  module: 'module', state: 'state', provider: 'provider', plan: 'plan',
}

/** What the server says it did. `skipped` can be longer than the dialog predicted: a name taken
 *  between opening this and pressing Apply is only discovered server-side. */
interface BaselineResponse {
  created: PolicyItem[]
  skipped: Array<{ name: string; artefact_type: PolicyArtefactType; reason: string }>
  rescan?: string
}

export interface BaselineApplied {
  created: number
  skipped: number
  rescan?: string
  /** Which artefacts gained a policy, so the caller knows whose list to refetch. */
  artefacts: PolicyArtefactType[]
}

interface Props {
  orgId: string
  /** Scope to one artefact (an artefact tab's button). Absent ⇒ all three side by side, which is
   *  what the Policies page offers an org that has no policies at all. */
  artefactType?: PolicyArtefactType
  /** The org's current policies, used to mark what it already has. The caller already holds this
   *  list, and fetching it again here would be a second GET of the same thing. */
  existing: readonly PolicyItem[]
  onClose: () => void
  onApplied: (result: BaselineApplied) => void
}

/**
 * The curated baseline picker: 3-5 recommended policies per artefact, ticked by default, applied
 * in one request.
 *
 * This is what replaced the policy set that used to be seeded into every new org at creation
 * time. The difference that matters is consent: the same policies, chosen. So two things are
 * load-bearing in the design -
 *
 *  - a policy the org already has by name is SHOWN, marked, and not selectable, rather than
 *    filtered out. Filtering it out would read as "we don't recommend this" when the truth is
 *    "you already have it", and an org that took a baseline policy and edited it must never find
 *    its version replaced by pressing a button labelled "add";
 *  - these land ENABLED (the only path in the product that does), because a dialog that adds five
 *    dormant policies has not done the thing its button promised.
 *
 * Opened from inside no form today, but every button still carries type="button": the policy
 * editor is a form, and this is a sibling of a dialog that opens inside it.
 */
export function BaselinePolicyModal({ orgId, artefactType, existing, onClose, onApplied }: Props) {
  const artefacts = useMemo(
    () => (artefactType ? [artefactType] : [...BASELINE_ARTEFACTS]),
    [artefactType],
  )
  const rows = useMemo(() => buildBaselineRows(existing, artefacts), [existing, artefacts])
  const [selected, setSelected] = useState<Set<string>>(() => defaultSelection(rows))
  const [busy, setBusy]         = useState(false)
  const [apiError, setApiError] = useState<string | null>(null)

  // Enforcement is tier-gated: the server checks the tier ONCE for the whole call, so asking for
  // it on a plan without OPA would 403 the lot. Below that tier the box is forced off, disabled
  // and explained, rather than letting the apply fail on something nobody chose.
  const settings = useOrgPolicySettings(orgId)
  const canEnforce = settings.data?.opaTier !== false
  const [wantEnforcing, setWantEnforcing] = useState(true)
  const enforcing = canEnforce && wantEnforcing

  const chosen = useMemo(() => toRequestPolicies(rows, selected), [rows, selected])
  const nothingToAdd = rows.every((r) => r.exists)

  function toggle(key: string) {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  function toggleColumn(columnRows: BaselineRow[], on: boolean) {
    setSelected((prev) => {
      const next = new Set(prev)
      for (const r of columnRows) {
        if (r.exists) continue
        if (on) next.add(r.key)
        else next.delete(r.key)
      }
      return next
    })
  }

  async function apply() {
    if (chosen.length === 0) return
    setBusy(true)
    setApiError(null)
    try {
      const res = await api.post<BaselineResponse>(`/api/orgs/${orgId}/policies/baseline`, {
        is_enforcing: enforcing,
        policies: chosen,
      })
      onApplied({
        created: res.created.length,
        skipped: res.skipped.length,
        ...(res.rescan ? { rescan: res.rescan } : {}),
        artefacts: [...new Set(res.created.map((p) => p.artefact_type))],
      })
    } catch (err) {
      // Left open with the selection intact: the usual failure here is the enforcement tier
      // guard, and the fix is to untick one box rather than to start again.
      setApiError((err as Error).message)
    } finally {
      setBusy(false)
    }
  }

  // The enforcement choice and the two actions go in the Modal's own `footer`, which sits
  // OUTSIDE the scrolling body: the panel then hugs its content (no empty half on a tall screen)
  // while Apply stays in view however long the set gets.
  const footer = (
    <div className={s.footer}>
      <label className={`${s.enforce} ${canEnforce ? '' : s.enforceDisabled}`}>
        <input
          type="checkbox"
          checked={enforcing}
          disabled={!canEnforce || busy}
          onChange={(e) => setWantEnforcing(e.target.checked)}
        />
        Enforce immediately
      </label>
      <span className={s.enforceNote}>
        {!canEnforce
          ? 'Enforcement requires a Business or Enterprise plan, so these will be added as advisory.'
          : (enforcing
            ? 'Failures will block downloads, applies and provider files.'
            : 'Failures are reported only (advisory). You can switch any policy to enforcing later.')}
      </span>
      <div className={s.actions}>
        <button type="button" className="btn btn-ghost btn-sm" disabled={busy} onClick={onClose}>
          Dismiss
        </button>
        <button
          type="button"
          className="btn btn-primary btn-sm"
          disabled={busy || chosen.length === 0}
          title={chosen.length === 0
            ? (nothingToAdd ? 'Nothing left to add' : 'Select at least one policy')
            : undefined}
          onClick={apply}
        >
          {busy ? <span className="spinner" /> : `Apply ${chosen.length} polic${chosen.length === 1 ? 'y' : 'ies'}`}
        </button>
      </div>
    </div>
  )

  return (
    <Modal
      track="policy_baseline_apply"
      title={artefactType ? `Recommended ${ARTEFACT_ADJECTIVE[artefactType]} policies` : 'Recommended policies'}
      onClose={onClose}
      maxWidth={artefactType ? 560 : 1040}
      footer={footer}
    >
      <div className={s.wrap}>
        <p className={s.intro}>
          {nothingToAdd
            ? 'Your organisation already has every recommended policy for this scope. Nothing here will change it.'
            : 'A starting set that covers the ground most organisations need. Everything new is ticked; untick anything you would rather author yourself. Policies you already have are marked and left alone.'}
        </p>

        <div className={s.columns}>
          {artefacts.map((artefact) => {
            const columnRows = rows.filter((r) => r.artefactType === artefact)
            const counts = columnCounts(columnRows, selected)
            const allOn = counts.selectable > 0 && counts.selected === counts.selectable
            return (
              <section key={artefact} className={s.column} aria-labelledby={`baseline-${artefact}`}>
                <div className={s.columnHead}>
                  <h3 id={`baseline-${artefact}`} className={s.columnTitle}>{ARTEFACT_LABEL[artefact]}</h3>
                  {counts.selectable > 0 ? (
                    <button type="button" className={s.toggleAll} onClick={() => toggleColumn(columnRows, !allOn)}>
                      {allOn ? 'none' : 'all'}
                    </button>
                  ) : (
                    <span className={s.columnCount}>all present</span>
                  )}
                </div>
                {counts.selectable > 0 && (
                  <span className={s.columnCount}>{counts.selected} of {counts.selectable} selected</span>
                )}
                {columnRows.length === 0 && (
                  <div className={s.columnEmpty}>No recommended policies for this scope yet.</div>
                )}
                {columnRows.map((row) => {
                  const on = selected.has(row.key)
                  return (
                    <label
                      key={row.key}
                      className={`${s.card} ${row.exists ? s.cardExists : (on ? s.cardOn : '')}`}
                      title={row.exists
                        ? 'Your organisation already has a policy with this name. Applying will not touch it.'
                        : undefined}
                    >
                      <input
                        type="checkbox"
                        className={s.box}
                        checked={on && !row.exists}
                        disabled={row.exists || busy}
                        onChange={() => toggle(row.key)}
                      />
                      <span className={s.body}>
                        <span className={s.cardHead}>
                          <span className={s.cardLabel}>{row.template.label}</span>
                          {row.exists && (
                            <span className="badge badge-neutral" style={{ fontSize: '0.58rem', whiteSpace: 'nowrap' }}>
                              already exists
                            </span>
                          )}
                        </span>
                        <span className={s.cardDesc}>{row.template.description}</span>
                      </span>
                    </label>
                  )
                })}
              </section>
            )
          })}
        </div>

        {apiError && <div className="feedback-error">{apiError}</div>}
      </div>
    </Modal>
  )
}

/** The toast line for an applied baseline, including how the change reached existing artefacts. */
export function baselineToast(result: BaselineApplied, rescanNote: (r?: string) => string): string {
  return applySummary(result.created, result.skipped) + (result.created > 0 ? rescanNote(result.rescan) : '')
}
